import { useState } from "react";
import { toast } from "react-hot-toast";
import { AlertTriangle, Download, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const WORKSPACE_NAME = "Aura Closet";
const CONFIRM_PHRASE = "DELETE";

export function DangerZoneSection() {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const handleExport = () => {
    toast.success("Export started — you'll receive an email when it's ready");
  };

  const canDelete = confirmText.trim().toUpperCase() === CONFIRM_PHRASE;

  const handleDelete = () => {
    if (!canDelete) return;
    toast.success("Workspace deletion queued (demo only)");
    setShowDeleteConfirm(false);
    setConfirmText("");
  };

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-rose-200/90 dark:border-rose-900/60 bg-rose-50/30 dark:bg-rose-950/20 shadow-xs">
      <div className="flex items-center justify-between border-b border-rose-200/70 dark:border-rose-900/40 bg-rose-100/40 dark:bg-rose-950/40 px-3.5 py-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400">
            <AlertTriangle className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-rose-800 dark:text-rose-300">
            Danger Zone
          </span>
        </div>
        <span className="text-[10px] font-medium text-rose-600/80 dark:text-rose-400/80">Irreversible workspace actions</span>
      </div>

      <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-3">
        {/* Export Data */}
        <div className="flex flex-col gap-3 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Export workspace data</p>
            <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">
              Download an archive of all conversations, customer contacts, and training configs.
            </p>
          </div>
          <Button type="button" variant="outline" className="h-9 px-4 text-xs font-semibold rounded-xl shadow-xs border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800" onClick={handleExport}>
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export data
          </Button>
        </div>

        {/* Delete Workspace */}
        <div className="space-y-3 rounded-xl border border-rose-200/80 dark:border-rose-900/40 bg-rose-50/20 dark:bg-rose-950/20 p-3 shadow-2xs">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold text-rose-900 dark:text-rose-300">Permanently delete workspace</p>
              <p className="text-[10px] text-rose-600/80 dark:text-rose-400/80 font-medium">
                Deletes {WORKSPACE_NAME} and resets all chatbot integrations. This action cannot be reversed.
              </p>
            </div>
            {!showDeleteConfirm && (
              <Button type="button" variant="destructive" className="h-9 px-4 text-xs font-semibold rounded-xl shadow-xs" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete workspace
              </Button>
            )}
          </div>

          {showDeleteConfirm && (
            <div className="space-y-2 border-t border-rose-200/60 dark:border-rose-900/40 pt-3">
              <Label htmlFor="delete-confirm" className="text-[10px] font-semibold text-rose-900 dark:text-rose-300">
                Type <span className="font-bold">{CONFIRM_PHRASE}</span> to confirm deletion
              </Label>
              <Input
                id="delete-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                className="h-8.5 text-xs border-rose-200 dark:border-rose-800 rounded-xl"
                autoFocus
              />
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="destructive" className="h-9 px-4 text-xs font-semibold rounded-xl shadow-xs" disabled={!canDelete} onClick={handleDelete}>
                  Permanently delete
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-9 px-4 text-xs rounded-xl"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setConfirmText("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
